import React, { useEffect, useMemo, useState } from "react";
import SwitchDates from "../../components/SwitchDates";
import {
  addWeeks,
  endOfWeek,
  format,
  isValid,
  startOfWeek,
  startOfDay,
  isSameDay,
  parseISO,
} from "date-fns";
import WeekOverviewTable from "./components/WeekOverviewTable";
import ForecastTable from "./components/ForecastTable";
import NotActive from "./components/NotActive";
import { getLanguage, isAdmin, isSupervisor } from "@helpers";
import { selectCurrentRole } from "@store/user/userSelectors";
import { selectCurrentProperty } from "@store/property/propertySelector";
import useAppSelector from "@hooks/useAppSelector";
import useAppDispatch from "@hooks/useAppDispatch";
import {
  deliverWeekReport,
  exportDayReportsToCSV,
  fetchDayReports,
} from "@store/dayReport/dayReportThunk";
import { selectWeekOverview } from "@store/dayReport/dayReportSelector";
import { fetchRateTypes } from "@store/rateType/rateTypeThunk";
import Button from "@components/base/button";
import Modal from "@components/base/modal";
import Breadcrumb from "@components/base/breadcrumb";
import { useLocation, useNavigate } from "react-router-dom";
import { NavLabels } from "@components/Layout";
import { useTranslation } from "react-i18next";
import { Tabs, Tab } from "@components/base/tabs";
import theme from "@styles/theme";
import NewIssueContainer from "@screens/IssueOverview/components/NewIssueContainer";
import { IssueType } from "@store/issue/types";
import { fetchSpaces } from "@store/space/spaceThunk";
import { fetchUsers } from "@store/user/userThunk";

const WeekOverviewScreen: React.FC = () => {
  const { t } = useTranslation();
  const locale = getLanguage();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const role = useAppSelector(selectCurrentRole);
  const property = useAppSelector(selectCurrentProperty);
  const [showModal, setShowModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [modelInfo, setModalInfo] = useState<{
    title: string;
    content: string[];
    description: string;
    type: string;
    button: string;
  }>({
    title: "",
    content: [],
    description: "",
    type: "",
    button: "",
  });
  const [activeTab, setActiveTab] = useState<React.Key>("0");

  const { search } = useLocation();
  const date = new URLSearchParams(search).get("date");

  const selectedWeek = useMemo(() => {
    if (!date) return new Date();
    const parsed = parseISO(date);
    return isValid(parsed) ? parsed : new Date();
  }, [date]);

  const { active, email, permission } = property!.settings.reporting;
  const start = startOfWeek(selectedWeek, { locale });
  const end = endOfWeek(selectedWeek, { locale });
  const startDate = format(start, "d LLLL yyyy", {
    locale,
  });
  const endDate = format(end, "d LLLL yyyy", { locale });
  const weekLabel = format(
    selectedWeek,
    `'${t("dayreport.week")}' w '(${startDate} - ${endDate})'`,
    {
      locale,
    },
  );

  const emailNotSet = email?.length === 0;
  const canSend = permission?.send?.includes(role) || isAdmin(role);
  const canCreateTasks = isSupervisor(role);
  const today = useMemo(() => startOfDay(new Date()), []);
  const dayReports = useAppSelector(selectWeekOverview);

  const getSyncTimestamp = () => {
    if (activeTab !== "1") return null;

    const dayReportsArray = !!dayReports ? Object.values(dayReports) : [];
    const todayReport = dayReportsArray.find((report) =>
      isSameDay(startOfDay(new Date(report.date)), today),
    );
    const syncReport = todayReport || dayReportsArray[0];

    return syncReport?.deliveredAt
      ? format(
          parseISO(syncReport.deliveredAt),
          "EEEE d MMMM yyyy 'om' HH:mm",
          { locale },
        )
      : format(today, "EEEE d MMMM yyyy 'om' 06:00", { locale });
  };

  const goToNext = (next: boolean) => {
    const nextDate = addWeeks(selectedWeek, next ? 1 : -1);
    navigate(`/day-reports/week?date=${format(nextDate, "yyyy-MM-dd")}`);
  };
  const showConfirm = async (type: "send" | "download") => {
    if (type === "send") {
      if (email.length === 0) goToSettings();
      setShowModal(true);
      setModalInfo({
        title: t("dayreport.send.title"),
        description: t("dayreport.send.description"),
        content: email,
        button: t("dayreport.send.button"),
        type: "send",
      });
    }
  };

  const goToSettings = () => {
    navigate("/settings/day-reports");
  };

  const sendWeekReport = () => {
    dispatch(deliverWeekReport(format(selectedWeek, "yyyy-MM-dd")));
    setShowModal(false);
  };

  useEffect(() => {
    dispatch(fetchRateTypes());
    const fetchStartDate = date || format(selectedWeek, "yyyy-MM-dd");
    dispatch(fetchDayReports({ start: fetchStartDate }));
  }, [date, property?.code, activeTab, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canCreateTasks) return;
    dispatch(fetchSpaces());
    dispatch(fetchUsers());
  }, [canCreateTasks, dispatch]);

  const exportWeekOverview = () => {
    dispatch(exportDayReportsToCSV({ start: format(start, "yyyy-MM-dd") }));
  };

  return (
    <>
      <Breadcrumb labels={[NavLabels.DayReports]} />
      <section data-test-id="hk-weekoverzicht">
        {(!active || (emailNotSet && canSend)) && (
          <NotActive
            buttonText={
              !active ? t("dayreport.manage") : t("dayreport.setupEmail")
            }
            info={!active ? t("dayreport.enableEmail") : t("dayreport.noEmail")}
            onClick={goToSettings}
          />
        )}
        <div className="row">
          <div className="controls">
            <SwitchDates
              label={weekLabel}
              goToNext={goToNext}
              showBadges={false}
              hasConstraints={false}
            />
            {activeTab === "1" && getSyncTimestamp() && (
              <div className="sync-timestamp">
                {t("forecast.lastSync")}: {getSyncTimestamp()}
              </div>
            )}
          </div>
          {canCreateTasks && activeTab === "0" && (
            <div className="button-wrapper">
              <Button
                onClick={() => setShowNewTaskModal(true)}
                kind="primary"
                disabled={!active}>
                {t("issue.addNewTask")}
              </Button>
              <Button
                onClick={exportWeekOverview}
                kind="secondary"
                disabled={!active}>
                {t("dayreport.send.export")}
              </Button>
              {canSend && (
                <a
                  className={`send-link ${!active ? "disabled" : ""}`}
                  onClick={!active ? undefined : () => showConfirm("send")}>
                  {t("dayreport.send.self")}
                </a>
              )}
            </div>
          )}
        </div>

        <div className="week-overview-tabs">
          <Tabs
            activeKey={activeTab}
            onChange={({ activeKey }) => {
              setActiveTab(activeKey as React.Key);
            }}>
            <Tab title={t("dayreport.weekOverview")}>
              <WeekOverviewTable />
            </Tab>
            <Tab title={t("dayreport.expectedOccupancy")}>
              <ForecastTable />
            </Tab>
          </Tabs>
        </div>
        <Modal
          title={modelInfo.title}
          buttonText={modelInfo.button}
          // TODO: when download functionality is implemented, set undefined as downloadFunction
          buttonAction={modelInfo.type === "send" ? sendWeekReport : undefined}
          isOpen={showModal}
          setShowModal={setShowModal}>
          <p>
            {modelInfo.description}
            {modelInfo.content.map((item) => (
              <li key={`modelInfo_${item}`}>{item}</li>
            ))}
          </p>
        </Modal>
        {showNewTaskModal && (
          <Modal
            title={t("issue.newTask")}
            setShowModal={setShowNewTaskModal}
            isOpen={showNewTaskModal}>
            <NewIssueContainer
              onClose={() => setShowNewTaskModal(false)}
              type={IssueType.HOUSEKEEPING}
            />
          </Modal>
        )}
      </section>

      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx>
        {`
          section {
            display: flex;
            flex: 1;
            flex-direction: column;
            padding: 16px 32px;
            overflow: scroll;
          }
          .row,
          .controls {
            flex-direction: row;
            display: flex;
            width: 100%;
            align-items: center;
            gap: 8px;
          }
          .controls {
            margin: 16px 0;
          }
          li {
            text-align: left;
            padding-left: 16px;
          }
          .modal-title {
            font-size: 24px;
            max-width: 80%;
            padding: 16px 0 8px 0;
            text-align: left;
            font-weight: 600;
          }
          .button-wrapper {
            display: flex;
            align-items: center;
            gap: 16px;
          }
          .send-link {
            cursor: pointer;
            color: ${theme.colors.primaryA};
            text-decoration: underline;
          }
          .send-link.disabled {
            cursor: not-allowed;
            opacity: 0.5;
            pointer-events: none;
          }
          .warning {
            margin-top: 16px;
            font-weight: 600;
          }
          p {
            text-align: left;
          }
          .hk-weekoverzicht-totals {
            width: 100%;
          }
          .hk-weekoverzicht-totals-title {
            padding-bottom: 16px;
          }
          .hk-weekoverzicht-totals > p {
            padding-bottom: 8px;
          }
          .sync-timestamp {
            font-size: 14px;
            color: ${theme.colors.contentSecondary};
            font-style: italic;
            padding: 10px 12px;
          }
          .week-overview-tabs :global([role="tabpanel"]) {
            padding-left: 0;
            padding-right: 0;
            padding-bottom: 0;
          }
        `}
      </style>
    </>
  );
};

export default WeekOverviewScreen;
